import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE } from '../config/media';
import { usePageContent } from '../lib/useContent';

/**
 * Per-page SEO. Wraps react-helmet-async with brand defaults and Open Graph.
 * `noindex` blocks the page from search indexing - used on /login.
 * `jsonLd` is an array of plain objects rendered as <script type="application/ld+json">.
 *
 * Phase 2 CMS integration: if `cmsSlug` is passed (e.g. "seo.about"), we
 * fetch `/api/content/{cmsSlug}` and overlay any admin-edited meta /
 * description / canonical / og_image / json_ld on top of the hard-coded
 * props. CMS values take priority; absent CMS values fall through to the
 * props the page passed in; absent props fall through to the brand
 * defaults in config/media.js. Loading state renders defaults so the
 * initial paint still has meta tags populated for crawlers.
 */

const toAbsoluteUrl = (url) => {
    if (!url) return url;
    if (/^https?:\/\//i.test(url)) return url;
    const base = (SITE.url || '').replace(/\/$/, '');
    return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
};

const normaliseJsonLd = (block) => {
    if (!block) return [];
    if (Array.isArray(block)) return block;
    return [block];
};

export const SEO = ({
    title,
    description,
    path = '/',
    noindex = false,
    jsonLd = [],
    image,
    cmsSlug,
}) => {
    // Fetch SEO overrides. When cmsSlug is absent (e.g. /login) the hook
    // is still called with a stable fallback key for React-hook-order
    // determinism, but returns an empty object (unknown slug -> 404 ->
    // cache stays null -> state defaults to {}).
    const cms = usePageContent(cmsSlug || '__noop__', { meta: {} });
    const metaOverride = (cms && cms.meta) || {};

    const effectiveTitle = metaOverride.meta_title || title;
    const effectiveDescription = metaOverride.meta_description || description;
    const effectiveImage = metaOverride.og_image || image;
    const effectiveCanonical = metaOverride.canonical || `${SITE.url}${path}`;

    const fullTitle = effectiveTitle ? `${effectiveTitle} - ${SITE.name}` : SITE.name;
    const desc = effectiveDescription || SITE.description;
    const absoluteImage = toAbsoluteUrl(effectiveImage);
    const mergedJsonLd = [...normaliseJsonLd(jsonLd), ...normaliseJsonLd(metaOverride.json_ld)];

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            <link rel="canonical" href={effectiveCanonical} />
            {noindex ? (
                <meta name="robots" content="noindex,nofollow" />
            ) : (
                <meta name="robots" content="index,follow" />
            )}

            {/* Open Graph */}
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={effectiveCanonical} />
            {absoluteImage ? <meta property="og:image" content={absoluteImage} /> : null}
            {absoluteImage ? <meta property="og:image:secure_url" content={absoluteImage} /> : null}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            {absoluteImage ? <meta name="twitter:image" content={absoluteImage} /> : null}

            {mergedJsonLd.map((obj, i) => (
                <script
                    key={`${(obj && obj['@type']) || 'jsonld'}-${i}`}
                    type="application/ld+json"
                >
                    {JSON.stringify(obj)}
                </script>
            ))}
        </Helmet>
    );
};

export default SEO;
